'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePathname, useRouter } from 'next/navigation';
import { ProfileService } from '@/services/profile.service';
import { isOnboardingExempt } from '@/lib/onboarding-exempt-paths';

/**
 * Redirects authenticated users who haven't completed onboarding
 * to the /onboarding page. Skips for auth pages and the onboarding page itself.
 *
 * Email verification is NOT enforced here. It used to hard-redirect every
 * unverified password-provider user off every route (except the exempt paths),
 * which walled them out of the whole product before they'd even seen it.
 * Verification is now only required at the point of an actual paid
 * purchase — see the check in CartDrawer's handlePay — so unverified users
 * can browse, view courses, and claim/take free tests freely.
 */
export function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setChecked(true); return; }

    // Skip check for exempt pages
    if (isOnboardingExempt(pathname)) {
      setChecked(true);
      return;
    }

    // Also skip for API routes
    if (pathname.startsWith('/api/')) {
      setChecked(true);
      return;
    }

    // Admins do not take exams, so student onboarding does not apply to them.
    // Enforcing it meant a freshly created admin was bounced to /onboarding and
    // could not reach /admin at all until they clicked through a flow built for
    // students.
    if (user?.role === 'ADMIN') {
      setChecked(true);
      return;
    }

    // Check onboarding status
    ProfileService.getProfile()
      .then((data) => {
        // Non-null by contract (fetchWithAuth throws rather than resolving null on
        // failure) — asserted rather than optionally chained so a genuine null would
        // still throw here and fall into the .catch below, exactly as it did before
        // this call had a real return type instead of `any`.
        if (!data!.onboardingCompleted) {
          // Preserve the query too, e.g. an invite link's /events/join?code=… —
          // so /onboarding can return the user there after they finish it instead
          // of stranding them on the dashboard home.
          router.replace(`/onboarding?redirect=${encodeURIComponent(pathname + window.location.search)}`);
        } else {
          setChecked(true);
        }
      })
      .catch(() => {
        // If profile check fails, don't block — let them through
        setChecked(true);
      });
  }, [isAuthenticated, authLoading, user?.role, pathname, router]);

  // Show nothing while checking (prevents flash of content)
  if (authLoading || (!checked && isAuthenticated && !isOnboardingExempt(pathname))) {
    return null;
  }

  return <>{children}</>;
}
